/**
 * useGetDropInSubRail — payload for the Drop-In contextual sidebar
 * (Following + Live Now + Recommended). Used by the Drop-In sub-rail
 * component on /drop-in and individual Drop pages.
 *
 * Logged-out callers get an empty `following` array; the rail still
 * renders with `liveNow` + `recommended`. Refetches every 60s so live
 * indicators stay in sync without a websocket.
 *
 * Mirrors the lazy DropInApi import pattern from useGetDropInDirectory so
 * local-dev compile doesn't break before client regen.
 */

import {useQuery} from "@tanstack/react-query";
import {useSelector} from "react-redux";
import type {IStore} from "@/redux/defaultStore";
import {useGetDropInReady} from "@/hooks/useGetDropInVisible";
import getConfig from "@/utils/getConfig";

export interface DropInSubRailItem {
    id: string;
    handle: string;
    displayName: string;
    profileImageUrl?: string;
    isLive: boolean;
    viewerCount?: number;
    gameName?: string;
    lastLiveAt?: number;
}

export interface DropInSubRailData {
    following: DropInSubRailItem[];
    liveNow: DropInSubRailItem[];
    recommended: DropInSubRailItem[];
}

export function useGetDropInSubRail() {
    /*
     * Auth-dependent queryKey — when the user logs in / out, React Query
     * sees a key change and refetches. Without this, an initial
     * anonymous response stays cached after sign-in and the user's
     * Following list never surfaces. Keying on the token presence
     * (boolean) rather than the token value avoids cache misses on
     * routine refresh.
     */
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const isLoggedIn = !!fullToken?.token;
    /*
     * Gate the poll on the resolved Drop-In killswitch. Without this the
     * 60s interval keeps hitting /drop-in/sub-rail after an admin toggles
     * Drop-In off, and every call 404s (the backend route returns 404
     * when isDropInVisible() is false) — visible console noise on every
     * page that mounts the rail. useGetDropInReady waits for the config
     * to resolve before enabling, so a disabled feature never fires the
     * speculative request.
     */
    const dropInReady = useGetDropInReady();
    return useQuery<DropInSubRailData | null>({
        queryKey: ["drop-in-sub-rail", isLoggedIn],
        enabled: dropInReady,
        queryFn: async () => {
            /*
             * Dynamic import + cast: @devour/client may not yet export
             * DropInApi.getDropInSubRail if the client hasn't been
             * regenerated since this endpoint shipped. Returning null on
             * the missing-class branch keeps the rail in a clean empty
             * state rather than failing to compile. Once the client regen
             * lands, this should be promoted to a static import + the
             * cast removed.
             */
            const clientModule = await import("@devour/client");
            const DropInApi = (clientModule as Record<string, unknown>).DropInApi as
                | (new (config: unknown) => {
                    getDropInSubRail: () => Promise<DropInSubRailData>;
                })
                | undefined;
            if (!DropInApi) return null;
            const api = new DropInApi(getConfig());
            if (typeof api.getDropInSubRail !== "function") return null;
            return await api.getDropInSubRail();
        },
        staleTime: 60 * 1000,
        refetchInterval: 60 * 1000,
        /*
         * `refetchOnWindowFocus` would stack on top of the 60s interval —
         * every tab focus would fire another /drop-in/sub-rail call
         * (three parallel Mongo queries per call) regardless of the poll
         * cadence. 60s freshness is adequate for a sidebar that shows
         * live-status indicators; the interval alone keeps it current.
         */
        refetchOnWindowFocus: false,
    });
}
